import "./JobFilterComponent.scss";
import {useCallback, useEffect, useState, useMemo} from "react";
import useDebounceCallback from "../../../hooks/useDebounceCallback";
import {useSelector} from "react-redux";
import {StateParams} from "../../../store/reducers";
import CustomSelectComponent from "../../../components/custom-select/CustomSelectComponent";
import InputComponent from "../../../components/ant-component/input/InputComponent";
import {ImageConfig} from "../../../constants";
import {useNavigate} from "react-router-dom";
import moment from "moment/moment";
import { FilterOutlined, CalendarOutlined, DollarOutlined } from '@ant-design/icons';
import { Badge, Drawer, Button as AntButton, Radio, DatePicker, Slider } from 'antd';
import dayjs from 'dayjs';
import uniqBy from "lodash/uniqBy";
import { JobTypeCountsMap } from "../jobBoardSearchParams";


export interface FilterStateFromUrl {
    selectedProfessions: string[];
    selectedSpeciality: string[];
    selectedStates: string[];
    citySearch: string;
    startDate: Date | null;
    endDate: Date | null;
    minSalary: number | null;
    maxSalary: number | null;
    hotJobFilter: string;
}

interface JobFilterComponentProps {
    setFilterData?: any;
    setJobList?: any;
    activeJobType?: string;
    filterStateFromUrl?: FilterStateFromUrl;
    jobTypeCounts?: JobTypeCountsMap;
    onJobTypeChange?: (type: string) => void;
}

const JOB_TYPE_TAB_LABELS: Record<string, string> = {
    all: 'All',
    contract: 'Contract',
    permanent: 'Permanent',
    per_diem: 'Per Diem',
    locum: 'Locum',
};

const formatJobTypeTabLabel = (value: string): string => {
    if (JOB_TYPE_TAB_LABELS[value]) return JOB_TYPE_TAB_LABELS[value];
    return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const JobFilterComponent = (props: JobFilterComponentProps) => {
    const { setJobList, setFilterData, activeJobType, filterStateFromUrl, jobTypeCounts, onJobTypeChange } = props;
    const navigate = useNavigate();
    const [selectedProfessions, setSelectedProfessions] = useState<string[]>(() => filterStateFromUrl?.selectedProfessions ?? []);
    const [selectedSpeciality, setSelectedSpeciality] = useState<string[]>(() => filterStateFromUrl?.selectedSpeciality ?? []);
    const [selectedStates, setSelectedStates] = useState<string[]>(() => filterStateFromUrl?.selectedStates ?? []);
    const [citySearch, setCitySearch] = useState<string>(() => filterStateFromUrl?.citySearch ?? '');
    const [startDate, setStartDate] = useState<any>(() => filterStateFromUrl?.startDate ?? null);
    const [endDate, setEndDate] = useState<any>(() => filterStateFromUrl?.endDate ?? null);
    const [minSalary, setMinSalary] = useState<any>(() => filterStateFromUrl?.minSalary ?? null);
    const [maxSalary, setMaxSalary] = useState<any>(() => filterStateFromUrl?.maxSalary ?? null);
    const [hotJobFilter, setHotJobFilter] = useState<string>(() => filterStateFromUrl?.hotJobFilter ?? 'all');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { hcpTypes, regions, specialitiesMasterList } = useSelector((state: StateParams) => state.meta);
    const [professionList, setProfessionList] = useState<any[]>([]);
    const [stateList, setStateList] = useState<any[]>([]);

    const specialityListFormatted = useMemo(() => {
        return uniqBy(specialitiesMasterList?.map((item: any) => ({
            label: item.title,
            value: item.code
        })), 'value') || [];
    }, [specialitiesMasterList]);

    // Count of active drawer filters (excludes search and hot job — those are in main bar)
    const drawerFilterCount = useMemo(() => {
        let count = 0;
        if (selectedProfessions.length > 0) count++;
        if (selectedSpeciality.length > 0) count++;
        if (selectedStates.length > 0) count++;
        if (startDate || endDate) count++;
        if (minSalary || maxSalary) count++;
        return count;
    }, [selectedProfessions, selectedSpeciality, selectedStates, startDate, endDate, minSalary, maxSalary]);

    // Sync local state from URL when it changes
    useEffect(() => {
        if (!filterStateFromUrl) return;
        setSelectedProfessions(filterStateFromUrl.selectedProfessions ?? []);
        setSelectedSpeciality(filterStateFromUrl.selectedSpeciality ?? []);
        setSelectedStates(filterStateFromUrl.selectedStates ?? []);
        setCitySearch(filterStateFromUrl.citySearch ?? '');
        setStartDate(filterStateFromUrl.startDate ?? null);
        setEndDate(filterStateFromUrl.endDate ?? null);
        setMinSalary(filterStateFromUrl.minSalary ?? null);
        setMaxSalary(filterStateFromUrl.maxSalary ?? null);
        setHotJobFilter(filterStateFromUrl.hotJobFilter ?? 'all');
    }, [filterStateFromUrl]);

    useEffect(() => {
        if (hcpTypes) {
            setProfessionList(hcpTypes.map((type: any) => ({ label: type.title, value: type.code })));
        }
    }, [hcpTypes]);

    useEffect(() => {
        if (regions) {
            setStateList(regions.map((type: any) => ({ label: type.title, value: type.code })));
        }
    }, [regions]);

    const handleProfessionSelect = useCallback((values: any) => {
        setSelectedProfessions(values as string[]);
    }, []);

    const handleClearAll = useCallback(() => {
        setSelectedSpeciality([]);
        setSelectedProfessions([]);
        setSelectedStates([]);
        setStartDate(null);
        setEndDate(null);
        setMinSalary(null);
        setMaxSalary(null);
        setHotJobFilter('all');
    }, []);

    const buildFilterPayload = useCallback(() => {
        const updatedFilterData: any = { page: 1 };
        if (activeJobType && activeJobType !== 'all') updatedFilterData.job_type = activeJobType;
        if (selectedProfessions.length > 0) updatedFilterData.staff_types = selectedProfessions;
        if (selectedSpeciality?.length > 0 && selectedSpeciality[0] !== 'None') updatedFilterData.specialities = selectedSpeciality;
        if (selectedStates.length > 0) updatedFilterData.states = selectedStates;
        updatedFilterData.search = citySearch && citySearch.trim().length > 0 ? citySearch.trim() : '';
        if (startDate) updatedFilterData.start_date = moment(startDate).format('YYYY-MM-DD');
        if (endDate) updatedFilterData.end_date = moment(endDate).format('YYYY-MM-DD');
        if (minSalary !== null && minSalary !== undefined && minSalary > 0) updatedFilterData.min_salary = minSalary;
        if (maxSalary !== null && maxSalary !== undefined && maxSalary < 5000) updatedFilterData.max_salary = maxSalary;
        if (hotJobFilter === 'hot') updatedFilterData.is_hot_job = true;
        return updatedFilterData;
    }, [selectedStates, selectedProfessions, selectedSpeciality, citySearch, startDate, endDate, minSalary, maxSalary, hotJobFilter, activeJobType]);

    const applySearchFilter = useCallback((searchValue: string) => {
        const updatedFilterData: any = { page: 1 };
        if (activeJobType && activeJobType !== 'all') updatedFilterData.job_type = activeJobType;
        if (selectedProfessions.length > 0) updatedFilterData.staff_types = selectedProfessions;
        if (selectedSpeciality?.length > 0 && selectedSpeciality[0] !== 'None') updatedFilterData.specialities = selectedSpeciality;
        if (selectedStates.length > 0) updatedFilterData.states = selectedStates;
        updatedFilterData.search = searchValue && searchValue.trim().length > 0 ? searchValue.trim() : '';
        if (startDate) updatedFilterData.start_date = moment(startDate).format('YYYY-MM-DD');
        if (endDate) updatedFilterData.end_date = moment(endDate).format('YYYY-MM-DD');
        if (minSalary !== null && minSalary !== undefined && minSalary > 0) updatedFilterData.min_salary = minSalary;
        if (maxSalary !== null && maxSalary !== undefined && maxSalary < 5000) updatedFilterData.max_salary = maxSalary;
        if (hotJobFilter === 'hot') updatedFilterData.is_hot_job = true;
        setFilterData(updatedFilterData);
    }, [activeJobType, selectedProfessions, selectedSpeciality, selectedStates, startDate, endDate, minSalary, maxSalary, hotJobFilter, setFilterData]);

    const debouncedApplySearch = useDebounceCallback(applySearchFilter, 400);

    const handleApplyFilters = useCallback(() => {
        setJobList([]);
        navigate('/job');
        setFilterData(buildFilterPayload());
    }, [buildFilterPayload, navigate, setFilterData, setJobList]);

    const handleDrawerClose = useCallback(() => {
        handleApplyFilters();
        setDrawerOpen(false);
    }, [handleApplyFilters]);

    const handleDrawerCancel = useCallback(() => {
        if (filterStateFromUrl) {
            setSelectedProfessions(filterStateFromUrl.selectedProfessions ?? []);
            setSelectedSpeciality(filterStateFromUrl.selectedSpeciality ?? []);
            setSelectedStates(filterStateFromUrl.selectedStates ?? []);
            setCitySearch(filterStateFromUrl.citySearch ?? '');
            setStartDate(filterStateFromUrl.startDate ?? null);
            setEndDate(filterStateFromUrl.endDate ?? null);
            setMinSalary(filterStateFromUrl.minSalary ?? null);
            setMaxSalary(filterStateFromUrl.maxSalary ?? null);
            setHotJobFilter(filterStateFromUrl.hotJobFilter ?? 'all');
        }
        setDrawerOpen(false);
    }, [filterStateFromUrl]);

    const handleHotJobFilterChange = useCallback((value: string) => {
        setHotJobFilter(value);
        setJobList([]);
        navigate('/job');

        const updatedFilterData: any = { page: 1 };
        if (activeJobType && activeJobType !== 'all') updatedFilterData.job_type = activeJobType;
        if (selectedProfessions.length > 0) updatedFilterData.staff_types = selectedProfessions;
        if (selectedSpeciality?.length > 0 && selectedSpeciality[0] !== 'None') updatedFilterData.specialities = selectedSpeciality;
        if (selectedStates.length > 0) updatedFilterData.states = selectedStates;
        updatedFilterData.search = citySearch && citySearch.trim().length > 0 ? citySearch.trim() : '';
        if (startDate) updatedFilterData.start_date = moment(startDate).format('YYYY-MM-DD');
        if (endDate) updatedFilterData.end_date = moment(endDate).format('YYYY-MM-DD');
        if (minSalary !== null && minSalary !== undefined && minSalary > 0) updatedFilterData.min_salary = minSalary;
        if (maxSalary !== null && maxSalary !== undefined && maxSalary < 5000) updatedFilterData.max_salary = maxSalary;
        if (value === 'hot') updatedFilterData.is_hot_job = true;

        setFilterData(updatedFilterData);
    }, [activeJobType, selectedStates, selectedProfessions, setFilterData, setJobList, selectedSpeciality, citySearch, startDate, endDate, minSalary, maxSalary, navigate]);

    const hotJobFilterOptions = [
        { label: 'All', value: 'all' },
        { label: '🔥', value: 'hot' }
    ];

    const jobTypeTabs = useMemo(() => {
        if (!jobTypeCounts) return [];
        return Object.entries(jobTypeCounts)
            .filter(([, count]) => count != null)
            .map(([value, count]) => ({
                value,
                label: formatJobTypeTabLabel(value),
                count,
            }));
    }, [jobTypeCounts]);

    return (
        <div className='job-filter-component'>
            {/* Row 1: Hot job toggle */}
            <div className='filter-row-1'>
                <Radio.Group
                    value={hotJobFilter}
                    onChange={(e) => handleHotJobFilterChange(e.target.value)}
                    buttonStyle="solid"
                    size="large"
                >
                    {hotJobFilterOptions.map((option) => (
                        <Radio.Button key={option.value} value={option.value}>
                            {option.label}
                        </Radio.Button>
                    ))}
                </Radio.Group>
            </div>

            {/* Row 2: Search + Filters button */}
            <div className='filter-row-2'>
                <div className='search-wrapper'>
                    <InputComponent
                        value={citySearch}
                        onChange={(value) => {
                            setCitySearch(value);
                            debouncedApplySearch(value);
                        }}
                        onPressEnter={handleApplyFilters}
                        placeholder="Search"
                        allowClear={true}
                        prefix={<ImageConfig.SearchBlackIcon />}
                        size="large"
                    />
                </div>
                <Badge count={drawerFilterCount} offset={[-6, 6]}>
                    <AntButton
                        icon={<FilterOutlined />}
                        size="large"
                        onClick={() => setDrawerOpen(true)}
                        className="filters-btn"
                    >
                        Filters
                    </AntButton>
                </Badge>
            </div>

            {/* Row 3: Job type chips (from counts API keys only) */}
            {jobTypeTabs.length > 0 && (
                <div className='filter-row-3'>
                    {jobTypeTabs.map(({ label, value, count }) => (
                        <button
                            key={value}
                            className={`job-type-chip ${activeJobType === value ? 'active' : ''}`}
                            onClick={() => onJobTypeChange?.(value)}
                        >
                            {label} ({count})
                        </button>
                    ))}
                </div>
            )}

            {/* Filters Drawer */}
            <Drawer
                title="Filters"
                placement="right"
                width={window.innerWidth <= 768 ? '100%' : 400}
                open={drawerOpen}
                onClose={handleDrawerCancel}
                className="filters-drawer"
                footer={
                    <div className="drawer-footer">
                        <AntButton
                            danger
                            size="large"
                            onClick={handleClearAll}
                        >
                            Clear All
                        </AntButton>
                        <AntButton
                            type="primary"
                            size="large"
                            onClick={handleDrawerClose}
                        >
                            Apply Filters
                        </AntButton>
                    </div>
                }
            >
                <div className="drawer-filter-content">
                    <div className="drawer-filter-section">
                        <CustomSelectComponent
                            options={professionList || []}
                            values={selectedProfessions}
                            onChange={(values: any) => handleProfessionSelect(values)}
                            isMultiple={true}
                            search={true}
                            placeholder="Select Professions"
                            label="Profession"
                        />
                    </div>
                    <div className="drawer-filter-section">
                        <CustomSelectComponent
                            options={specialityListFormatted}
                            values={selectedSpeciality}
                            onChange={(values: any) => setSelectedSpeciality(values as string[])}
                            isMultiple={true}
                            search={true}
                            placeholder="Select Speciality"
                            label="Speciality"
                        />
                    </div>
                    <div className="drawer-filter-section">
                        <CustomSelectComponent
                            options={stateList || []}
                            values={selectedStates}
                            onChange={(values) => setSelectedStates(values as string[])}
                            isMultiple={true}
                            search={true}
                            placeholder="Select States"
                            label="State"
                        />
                    </div>
                    <div className="drawer-filter-section">
                        <div className="drawer-filter-label">Date Range</div>
                        <DatePicker.RangePicker
                            value={[startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null]}
                            onChange={(dates) => {
                                setStartDate(dates?.[0]?.toDate() ?? null);
                                setEndDate(dates?.[1]?.toDate() ?? null);
                            }}
                            placeholder={['Start date', 'End date']}
                            format="MMM DD, YYYY"
                            suffixIcon={<CalendarOutlined />}
                            size="large"
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="drawer-filter-section">
                        <div className="drawer-filter-label">Pay Range</div>
                        <div className="pay-range-display">
                            <DollarOutlined />
                            <span>${minSalary ?? 0} - ${maxSalary ?? 5000}/hr</span>
                        </div>
                        <div className="slider-wrapper">
                            <Slider
                                range
                                min={0}
                                max={5000}
                                step={10}
                                value={[minSalary ?? 0, maxSalary ?? 5000]}
                                onChange={(value) => {
                                    const [min, max] = value as [number, number];
                                    setMinSalary(min);
                                    setMaxSalary(max);
                                }}
                                tooltip={{ formatter: (value) => `$${value}/hr` }}
                            />
                        </div>
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default JobFilterComponent;
